/**
 * Created: 2026-04-08
 * Initial commit: Split SlotsPage into SlotsPage/ directory (feat/event/mr-10)
 * Updated: 2026-04-12 — replaced DataGridPro with pure CSS Grid (feat/event/mr-14)
 *
 * Slot grid management page — orchestrates date selection, queries, drag
 * selection, slot actions, and dialogs.
 */

import { gql } from '@apollo/client'
import { useQuery } from '@apollo/client/react'
import RefreshIcon from '@mui/icons-material/Refresh'
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Typography,
} from '@mui/material'
import { computeSpanBlocks } from '@tc-app/shared/slots/mergeSlots'
import { computeDisplayHours } from '@tc-app/shared/slots/slotHours'
import { SlotStatus } from '@tc-app/shared/slots/types'
import { format, isBefore, isToday } from 'date-fns'
import { useMemo, useState } from 'react'
import AppointmentDialog from '../../components/AppointmentDialog'
import DateNavigation from '../../components/DateNavigation'
import EventDialog from '../../components/EventDialog'
import EventFormDialog from '../../components/EventFormDialog'
import OrderDialog from '../../components/OrderDialog'
import { SiteDocument, SlotsDocument, SlotTemplatesDocument } from '../../gql/graphql'
import { useRole } from '../../hooks'
import SlotGrid from './SlotGrid'
import styles from './SlotGrid.module.css'
import type { SlotMeta } from './types'
import { useDragSelection } from './useDragSelection'
import { useSlotActions } from './useSlotActions'

gql`
  query Slots($siteId: UUID!, $date: Date!) {
    slots(siteId: $siteId, date: $date) {
      courtId
      startTime
      price
      status
      reason
      orderId
      eventId
      event {
        gameFormat
        minRating
        maxRating
        drawSize
        numSignups
        fee
      }
      coaching {
        sessionId
        seats
        attendees {
          status
          student {
            name
          }
        }
      }
    }
  }

`

/** Current hour in China timezone (0-23) */
function getChinaHour() {
  return (new Date().getUTCHours() + 8) % 24
}

/** Today's date in China timezone as a Date (midnight local) */
function getChinaToday() {
  const now = new Date()
  const chinaMs = now.getTime() + (8 * 60 + now.getTimezoneOffset()) * 60_000
  const china = new Date(chinaMs)
  return new Date(china.getFullYear(), china.getMonth(), china.getDate())
}

const STATUS_LEGEND: {
  label: string
  bg: string
  color: string
}[] = [
  { label: '保留', bg: '#fff3e0', color: '#e65100' },
  { label: '活动', bg: '#ede7f6', color: '#4527a0' },
  { label: '预订/课程', bg: '#e8f5e9', color: '#2e7d32' },
  { label: '锁定', bg: '#e0e0e0', color: '#424242' },
]

export default function SlotsPage() {
  const { currentSite } = useRole()
  const [selectedDate, setSelectedDate] = useState<Date>(new Date())

  const {
    data: sitesData,
    loading: sitesLoading,
    error: sitesError,
  } = useQuery(SiteDocument, {
    variables: { siteId: currentSite?.id as string },
    skip: !currentSite,
    fetchPolicy: 'cache-first',
  })

  const {
    data: slotsData,
    loading: slotsLoading,
    error: slotsError,
    refetch: slotsRefetch,
  } = useQuery(SlotsDocument, {
    variables: {
      siteId: currentSite?.id as string,
      date: format(selectedDate, 'yyyy-MM-dd'),
    },
    skip: !currentSite,
    fetchPolicy: 'no-cache',
  })

  const { data: templatesData } = useQuery(SlotTemplatesDocument, {
    variables: { siteId: currentSite?.id as string },
    skip: !currentSite,
    fetchPolicy: 'cache-first',
  })

  const courts = useMemo(() => {
    const courtsList = sitesData?.site.courts ?? []
    return [...courtsList].sort((a, b) => a.displayOrder - b.displayOrder)
  }, [sitesData])

  const { slotLookup, hours } = useMemo(() => {
    const slots = slotsData?.slots ?? []
    const lookup = new Map<string, SlotMeta>()
    for (const s of slots) {
      const chinaHour = (new Date(s.startTime).getUTCHours() + 8) % 24
      lookup.set(`${chinaHour}-${s.courtId}`, {
        price: s.price ?? '',
        status: s.status,
        startTime: s.startTime,
        reason: s.reason ?? undefined,
        orderId: s.orderId ?? undefined,
        eventId: s.eventId ?? undefined,
        event: s.event
          ? {
              gameFormat: s.event.gameFormat,
              minRating: s.event.minRating ?? undefined,
              maxRating: s.event.maxRating ?? undefined,
              drawSize: s.event.drawSize ?? undefined,
              numSignups: s.event.numSignups,
              fee: s.event.fee,
            }
          : undefined,
        sessionId: s.coaching?.sessionId,
        coaching: s.coaching
          ? {
              sessionId: s.coaching.sessionId,
              seats: s.coaching.seats,
              attendees: s.coaching.attendees.map((a) => ({
                status: a.status,
                name: a.student.name ?? undefined,
              })),
            }
          : undefined,
      })
    }
    return {
      slotLookup: lookup,
      hours: computeDisplayHours(
        slots.map((s) => s.startTime),
        templatesData?.slotTemplates ?? [],
        selectedDate,
      ),
    }
  }, [slotsData, templatesData, selectedDate])

  // Compute span blocks (merged cells), covered cells, and span maps for drag selection.
  // Merges both vertically (consecutive hours, same court) and horizontally (adjacent courts)
  // when cells share the same orderId, eventId, sessionId, or reason.
  const { spanBlocks, coveredCells, spanKeyMap, spanGroupCells } = useMemo(
    () => computeSpanBlocks(courts, hours, slotLookup),
    [courts, slotLookup, hours],
  )

  const pastHourThreshold = useMemo(() => {
    const chinaToday = getChinaToday()
    if (isBefore(selectedDate, chinaToday)) return 24
    if (
      isToday(chinaToday) &&
      format(selectedDate, 'yyyy-MM-dd') === format(chinaToday, 'yyyy-MM-dd')
    )
      return getChinaHour()
    return 0
  }, [selectedDate])

  const courtIds = useMemo(() => courts.map((c) => c.id), [courts])

  const {
    selectedCells,
    setSelectedCells,
    selectedKeys,
    gridRef,
    handleMouseDown,
    handleMouseMove,
    handleMouseUp,
  } = useDragSelection({
    courtIds,
    pastHourThreshold,
    slotLookup,
    spanKeyMap,
    spanGroupCells,
    dragHighlightClass: styles.dragHighlight,
  })

  const handleDateChange = (newDate: Date) => {
    setSelectedDate(newDate)
    setSelectedCells([])
  }

  const refetchSlots = () => {
    if (!currentSite) return
    return slotsRefetch()
  }

  const {
    hasOpen,
    hasReserved,
    canCreateEvent,
    eventSlots,
    mutating,
    reasonDialogOpen,
    setReasonDialogOpen,
    reasonInput,
    setReasonInput,
    priceDialogOpen,
    setPriceDialogOpen,
    priceInput,
    setPriceInput,
    handleReserveOpen,
    handleReserveConfirm,
    handleRelease,
    handleChangePriceOpen,
    handleChangePriceConfirm,
  } = useSlotActions({
    selectedCells,
    setSelectedCells,
    slotLookup,
    courts,
    slotsRefetch: refetchSlots,
  })

  // Dialog state
  const [orderDialogId, setOrderDialogId] = useState<string | null>(null)
  const [eventDialogId, setEventDialogId] = useState<string | null>(null)
  const [sessionDialogId, setSessionDialogId] = useState<string | null>(null)
  const [createEventOpen, setCreateEventOpen] = useState(false)

  const loading = sitesLoading || slotsLoading
  const error = sitesError || slotsError

  const handleCellClick = (hour: number, courtId: string) => {
    const meta = slotLookup.get(`${hour}-${courtId}`)
    if (meta?.status === SlotStatus.Booked && meta.orderId) {
      setOrderDialogId(meta.orderId)
    } else if (meta?.eventId) {
      setEventDialogId(meta.eventId)
    } else if (meta?.sessionId) {
      setSessionDialogId(meta.sessionId)
    }
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: 'calc(100vh - 64px - 48px)',
      }}
    >
      {/* Header: title + date navigation */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 2,
        }}
      >
        <Typography variant="h4">场地管理</Typography>
        <DateNavigation value={selectedDate} onChange={handleDateChange} />
      </Box>

      {/* Action buttons + legend */}
      <Box
        sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1, flexWrap: 'wrap' }}
      >
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            size="small"
            disabled={!hasOpen || mutating}
            onClick={handleReserveOpen}
          >
            保留
          </Button>
          <Button
            variant="outlined"
            size="small"
            disabled={!hasReserved || mutating}
            onClick={handleRelease}
          >
            释放
          </Button>
          <Button
            variant="outlined"
            size="small"
            disabled={!canCreateEvent || mutating}
            onClick={() => setCreateEventOpen(true)}
          >
            活动
          </Button>
          <Button
            variant="outlined"
            size="small"
            disabled={!hasOpen || mutating}
            onClick={handleChangePriceOpen}
          >
            改价
          </Button>
          <IconButton
            size="small"
            onClick={refetchSlots}
            disabled={mutating}
            sx={{ ml: 0.5 }}
          >
            <RefreshIcon fontSize="small" />
          </IconButton>
        </Box>
        <Box sx={{ display: 'flex', gap: 1.5, ml: 'auto' }}>
          {STATUS_LEGEND.map((item) => (
            <Box
              key={item.label}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                fontSize: '0.75rem',
              }}
            >
              <Box
                sx={{
                  width: 14,
                  height: 14,
                  borderRadius: '2px',
                  backgroundColor: item.bg,
                  border: '1px solid #ccc',
                }}
              />
              <Typography variant="caption" sx={{ color: item.color }}>
                {item.label}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>

      {error && <Alert severity="error">加载预约数据失败: {error.message}</Alert>}

      <SlotGrid
        courts={courts}
        hours={hours}
        slotLookup={slotLookup}
        coveredCells={coveredCells}
        spanBlocks={spanBlocks}
        pastHourThreshold={pastHourThreshold}
        selectedKeys={selectedKeys}
        loading={loading}
        gridRef={gridRef}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onCellClick={handleCellClick}
      />

      {/* Reserve reason dialog */}
      <Dialog
        open={reasonDialogOpen}
        onClose={() => setReasonDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>保留原因</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            label="原因"
            fullWidth
            multiline
            rows={3}
            value={reasonInput}
            onChange={(e) => setReasonInput(e.target.value.slice(0, 100))}
            slotProps={{
              htmlInput: { maxLength: 100 },
            }}
            error={
              reasonInput.length > 0 &&
              (reasonInput.length < 2 || reasonInput.startsWith('韵动吧'))
            }
            helperText={
              reasonInput.startsWith('韵动吧')
                ? '不允许以"韵动吧"开头'
                : reasonInput.length > 0 && reasonInput.length < 2
                  ? '至少输入2个字符'
                  : `${reasonInput.length}/100`
            }
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReasonDialogOpen(false)}>取消</Button>
          <Button
            onClick={handleReserveConfirm}
            variant="contained"
            disabled={reasonInput.length < 2 || reasonInput.startsWith('韵动吧')}
          >
            确认
          </Button>
        </DialogActions>
      </Dialog>

      {/* Change price dialog */}
      <Dialog
        open={priceDialogOpen}
        onClose={() => setPriceDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>修改价格</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            label="新价格 (元)"
            type="number"
            fullWidth
            value={priceInput}
            onChange={(e) => {
              const v = e.target.value
              if (v.includes('-')) return
              if (v === '' || (/^\d{0,3}(\.\d{0,2})?$/.test(v) && Number(v) <= 999)) {
                setPriceInput(v)
              }
            }}
            onKeyDown={(e) => {
              if (e.key === '-' || e.key === 'Minus') {
                e.preventDefault()
              }
            }}
            slotProps={{
              htmlInput: { min: 0, max: 999, step: 0.01 },
            }}
            sx={{
              mt: 1,
              '& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button':
                {
                  WebkitAppearance: 'none',
                  margin: 0,
                },
              '& input[type=number]': {
                MozAppearance: 'textfield',
              },
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPriceDialogOpen(false)}>取消</Button>
          <Button
            onClick={handleChangePriceConfirm}
            variant="contained"
            disabled={
              priceInput === '' || Number(priceInput) < 0 || Number(priceInput) > 999
            }
          >
            确认
          </Button>
        </DialogActions>
      </Dialog>

      <OrderDialog orderId={orderDialogId} onClose={() => setOrderDialogId(null)} />

      <AppointmentDialog
        sessionId={sessionDialogId}
        onClose={() => setSessionDialogId(null)}
      />

      {eventDialogId && (
        <EventDialog
          key={eventDialogId}
          eventId={eventDialogId}
          onClose={() => {
            setEventDialogId(null)
            refetchSlots()
          }}
        />
      )}

      {createEventOpen && (
        <EventFormDialog
          slots={eventSlots}
          onClose={() => setCreateEventOpen(false)}
          onSuccess={() => {
            setCreateEventOpen(false)
            setSelectedCells([])
            refetchSlots()
          }}
        />
      )}
    </Box>
  )
}
